import _ from 'lodash'
import { atom, useAtom } from 'jotai'
import { safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { getProposal, setProposal } from './useProposal'
import {
  getExtensionFromUrl,
  getHttpsImageLinksFromData,
  replaceImageLinksWithMapping,
  isProductImageUrl,
  stripProductImageHash
} from '../helpers'
import useCloneRequestController from './useCloneRequestController'
import useClientS3Upload from '../../../../../hooks/useClientS3Upload'

const downloadedAtom = atom(true)

const getBlobImageDimensionsImpl = (blob) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(blob)
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image for dimension check'))
    }
    img.src = url
  })

/** Ref for dimension lookup so tests can override. */
export const getBlobImageDimensionsRef = { current: getBlobImageDimensionsImpl }
const getBlobImageDimensions = (blob) => getBlobImageDimensionsRef.current(blob)

const webpBlobToJpegBlob = (blob) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(blob)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      canvas.toBlob(
        (jpegBlob) => (jpegBlob ? resolve(jpegBlob) : reject(new Error('toBlob failed'))),
        'image/jpeg',
        0.92
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image for webp→jpeg conversion'))
    }
    img.src = url
  })

// Result is { url, file?, failed, error?, dimensions?, isProductImage }.
const downloadAndPrepareImage = async (url, index, { signal, fetchWithAbort }) => {
  const isProductImage = isProductImageUrl(url)
  try {
    const [fetchErr, response] = await safeAwait(
      fetchWithAbort(stripProductImageHash(url), { signal })
    )
    if (fetchErr) {
      console.log('取得原始圖片失敗', url, fetchErr)
      const result = { url, failed: true, error: fetchErr, isProductImage }
      return result
    }

    const status = _.get(response, 'status', '')
    if (!response || !response.ok) {
      const err = new Error(`HTTP ${status}`)
      console.log('下載圖片失敗', url, err)
      const result = { url, failed: true, error: err, isProductImage }
      return result
    }

    const [blobErr, blobData] = await safeAwait(response.blob())
    if (blobErr) {
      console.log('讀取圖片 blob 失敗', url, blobErr)
      const result = { url, failed: true, error: blobErr, isProductImage }
      return result
    }

    let blob = blobData
    const ext = getExtensionFromUrl(url)
    const blobType = _.get(blob, 'type', '')
    const isWebp = ext === 'webp' || blobType === 'image/webp'
    if (isWebp) {
      const [webpErr, jpegBlob] = await safeAwait(webpBlobToJpegBlob(blob))
      if (webpErr) {
        console.log('webp 轉 jpeg 失敗', url, webpErr)
        const result = { url, failed: true, error: webpErr, isProductImage }
        return result
      }
      blob = jpegBlob
    }

    const [dimErr, dimensions] = await safeAwait(getBlobImageDimensions(blob))
    if (dimErr) {
      console.log('驗證圖片時發生錯誤', url, dimErr)
      const result = { url, failed: true, error: dimErr, isProductImage }
      return result
    }

    if (isProductImage && !getIsSquareImage(dimensions)) {
      console.log('商品圖不是正方形', url, dimensions)
      const err = new Error('商品圖不是正方形')
      const result = { url, failed: true, error: err, isProductImage }
      return result
    }

    const outExt = isWebp ? 'jpg' : ext
    const filename = `image_${index}.${outExt}`
    const file = new File([blob], filename, {
      type: _.get(blob, 'type', 'image/png')
    })
    return { url, file, failed: false, dimensions, isProductImage }
  } catch (err) {
    console.log('下載圖片流程內部錯誤', url, err)
    const result = { url, failed: true, error: err, isProductImage }
    return result
  }
}

const getIsSquareImage = (dimensions = { width: 0, height: 0 }) => {
  const { width, height } = dimensions
  return width === height
}

const getIsValidSquareImage = (r) => {
  const dimensions = _.get(r, 'dimensions', { width: 0, height: 0 })
  const { width = 0 } = dimensions
  return width >= 1000 && getIsSquareImage(dimensions)
}

const useProposalAssetsUpload = () => {
  const { signal, fetchWithAbort, getController } = useCloneRequestController()
  const [downloaded, setDownloaded] = useAtom(downloadedAtom)
  const { trigger: s3Trigger, isLoading, result } = useClientS3Upload(fetchWithAbort)

  const triggerUpload = async () => {
    const proposal = getProposal()
    if (proposal == null || _.isEmpty(proposal)) {
      return
    }

    const urls = getHttpsImageLinksFromData(proposal)
    const filePromises = urls.map((url, index) =>
      downloadAndPrepareImage(url, index, { signal, fetchWithAbort })
    )

    setDownloaded(false)
    const results = _.flow((results) => {
      const productImageResults = results.filter((r) => r.isProductImage)
      const hasAtLeastOneValidSquareProduct = productImageResults.some(getIsValidSquareImage)
      if (!hasAtLeastOneValidSquareProduct && productImageResults.length > 0) {
        const err = new Error('需要至少一張商品圖是 1000x1000 的尺寸')
        return results.map((r) => {
          if (r.isProductImage) {
            return { ...r, failed: true, error: err }
          }
          return r
        })
      }
      return results
    })(await Promise.all(filePromises))

    const validProductResults = results.filter(
      (r) => !r.failed && r.isProductImage && getIsValidSquareImage(r)
    )
    const singleValidProductUrl = _.get(validProductResults, '[0].url', '')
    const isSingleValidProductImage = _.size(validProductResults) === 1

    setDownloaded(true)

    const hasValidProductImages = validProductResults.length > 0
    const validFiles = results
      .filter(r => {
        if (!hasValidProductImages) {
          return r.file && !r.isProductImage
        }

        return r.file
      })
      .map((r) => r.file)
    let uploadResult = {}
    if (validFiles.length > 0) {
      const { error: triggerError, result: triggerResult } = await s3Trigger(
        validFiles,
        getController()
      )
      if (triggerError) {
        console.log('上傳圖片失敗', triggerError)
        const mapping = urls
          .reduce((acc, url) => {
            acc[url] = { url: '' }
            return acc
          }, {})
        const updated = replaceImageLinksWithMapping(proposal, mapping)
        setProposal(updated)
        return
      }
      uploadResult = triggerResult || {}
    }

    const mapping = results.reduce((acc, r) => {
      const newUrl = _.get(uploadResult, [r.file?.name, 'url'], '')
      acc[r.url] = {
        ...r,
        url: r.failed ? '' : newUrl
      }
      return acc
    }, {})
    let updated = replaceImageLinksWithMapping(proposal, mapping)
    if (validProductResults.length === 0) {
      updated = { ...updated, productImages: [] }
    } else if (isSingleValidProductImage && singleValidProductUrl) {
      const singleMappedUrl = _.get(mapping, [singleValidProductUrl, 'url'], '')
      if (singleMappedUrl) {
        const productImages = _.get(updated, 'productImages', [])
        updated = {
          ...updated,
          productImages: [
            singleMappedUrl,
            singleMappedUrl,
            ...productImages.filter(url => url !== singleMappedUrl)
          ]
        }
      }
    }
    setProposal(updated)
  }

  const uploadProgress = {
    uploaded: Object.values(result).filter((r) => r && r.url).length,
    total: Object.keys(result).length
  }

  return {
    triggerUpload,
    uploadProgress,
    isUploading: isLoading,
    downloaded
  }
}

export default useProposalAssetsUpload
